import { Flex, FormControl, FormErrorMessage, Text } from "@chakra-ui/react";
import { FormikHandlers, FormikHelpers } from "formik";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "../../../../../assets/css/autocomplete.css";
import { useSearchStatesCountriesMutation } from "store/geo.slice";
import AtsAutoComplete from "./Autocomplete";

interface fieldInterface {
  name: string;
  value: any;
  errors?: any;
  touched?: any;
}

interface addCompInt<T> {
  index?: number;
  handleChange: FormikHandlers["handleChange"];
  setFieldValue: FormikHelpers<T>["setFieldValue"];
  cityField?: fieldInterface;
  locationField?: fieldInterface;
  placeholder?: string;
  onSubmit: (data: T) => void;
  values: T;
}

interface GeoState {
  id: number;
  state: string;
  state_id: string;
  country: string;
  country_code: string;
}

interface GeoCountry {
  id: number;
  country: string;
  code: string;
}

interface LocationOption {
  label: string;
  value: string;
  type: "state" | "country";
  state?: string;
  state_id?: string;
  country: string;
  country_code: string;
}

const AddressComponent = <T,>({
  index = 0,
  setFieldValue,
  cityField,
  locationField,
  onSubmit,
  values,
  placeholder = "Location",
}: addCompInt<T>) => {
  const { geo_states, geo_countries } = useSelector((state: any) => ({
    geo_states: state.geolocation.geo_states as GeoState[],
    geo_countries: state.geolocation.geo_countries as GeoCountry[],
  }));

  const [itemLocation, setItemLocation] = useState<LocationOption[]>([]);
  const [reqGeo, resGeo] = useSearchStatesCountriesMutation();

  useEffect(() => {
    setGeoItem();
  }, [geo_states, geo_countries]);

  const setGeoItem = () => {
    const stateOptions: LocationOption[] = (geo_states || []).map(
      (item: GeoState) => ({
        label: [item?.state, item?.country].filter(Boolean).join(", "),
        value: item.state_id,
        type: "state",
        state: item.state,
        state_id: item.state_id,
        country: item.country,
        country_code: item.country_code,
      }),
    );

    const countryOptions: LocationOption[] = (geo_countries || []).map(
      (item: GeoCountry) => ({
        label: item.country,
        value: item.code,
        type: "country",
        country: item.country,
        country_code: item.code,
      }),
    );

    setItemLocation([...stateOptions, ...countryOptions]);
  };

  const geoLocationChange = debounce((e: any) => {
    const search = e.target.value;
    if (search.length > 2) {
      reqGeo({ search, limit: 25 });
    }
  }, 500);

  const handleOnSelect = (item: LocationOption) => {
    setFieldValue(cityField.name, item.label);
    setFieldValue(locationField.name, [item]);
  };

  return (
    <div className="row" key={`jobAdd-${index}`}>
      <FormControl isInvalid={Boolean(cityField.errors && cityField.touched)}>
        <AtsAutoComplete
          name={cityField.name}
          placeholder={placeholder}
          type="text"
          variant="outline"
          value={cityField.value}
          onChange={(e: any) => {
            setFieldValue(cityField.name, e.target.value);
          }}
          options={itemLocation}
          onKeyUp={(e: any) => geoLocationChange(e)}
          isLoading={resGeo.isLoading}
          onSelect={(item: LocationOption) => handleOnSelect(item)}
          setFieldValue={setFieldValue}
          onSubmit={onSubmit}
          values={values}
        />

        <FormErrorMessage>{String(cityField.errors)}</FormErrorMessage>
      </FormControl>
    </div>
  );
};

export default AddressComponent;
